/**
 * 시세 표시 모델 — PKA_PRD_v0.1 §5 "가격 데이터 기준" 의 **출력 필드**(§5.8)를
 * 화면이 그릴 수 있는 형태로 정의한다.
 *
 * ⚠ 이 모듈은 가격을 **계산하지 않는다.** 가중 중앙값·최근성 가중치·이상거래 제거는
 * 전부 서버(수집/집계) 몫이다. 여기 있는 건 Phase 2 더미이며, 서버 API가 붙으면
 * `EditionQuote` 타입만 남기고 생성부는 `src/api` 호출로 교체한다.
 *
 * PRD가 못박은 표시 규칙 중 화면이 지켜야 하는 것:
 *  - §5.0 언어판(한국판·영문판·일본판)을 하나의 시세로 **합치지 않는다** → 에디션 분리
 *  - §5.1·§5.2 KREAM 의 일본판·영문판 체결가는 본국 시세에 섞지 않고
 *    **국내 거래가로 나란히** 표시한다 → `EditionQuote.domestic`
 *  - §5.6 거래량이 부족하면 단일 숫자보다 **가격 범위**를 우선한다
 *  - §5.3 판매호가·구매희망가는 대표 시세에서 제외하고 **별도 표시**
 *  - §5.7 원본 통화와 원화 환산가를 **함께** 표시
 *  - §5.4 감정사·점수가 다르면 합치지 않는다 → 등급 행 분리
 */
import type { DummyCard, DummyFinish, DummyVariantPrice } from "./dummyCards";
import { DUMMY_CARDS } from "./dummyCards";

/** 지원 카드 에디션 (PRD §3.1 — 실거래 시장이 명확한 3판). */
export type Edition = "ko" | "en" | "ja";
export const EDITIONS: Edition[] = ["ko", "en", "ja"];

/** PRD §5.6 가격 신뢰도 4등급. */
export type PriceConfidence = "high" | "medium" | "low" | "none";

/** PRD §5.8 "가격 기준" — 실거래 / 집계 서비스 / 매장 판매가. */
export type PriceBasis = "trade" | "aggregate" | "retail";

export interface QuoteSource {
  /** 출처명 (KREAM·번개장터·TCGplayer …) */
  name: string;
  /** 이 출처에서 집계에 쓰인 거래 건수 */
  count: number;
}

/** 등급 한 종류의 시세 (PRD §5.4 — 감정사·점수가 다르면 별개 가격). */
export interface GradeQuote {
  graderCd: string;
  grdCd: string;
  prcAmt: number;
  /** 실거래가 없어 추세를 낼 수 없으면 null — 다른 등급 값을 빌려오지 않는다 */
  trend30Rt: number | null;
  /** 실거래 없이 다른 등급에서 배수로 **추정한** 값인지 (FN-018 기본 4종 채우기) */
  isEstimated?: boolean;
}

/**
 * 한국 내 거래가 (PRD §5.1 5순위 · §5.2 5순위).
 *
 * 일본판·영문판 카드가 **국내에서** 체결된 가격(KREAM)이다. 본국 시세에 섞으면
 * 한국 프리미엄이 현지 시세를 밀어올려 **두 숫자가 다 틀리게 되므로** 분리해 둔다.
 * 분리한 결과로 남는 두 값의 차이가 곧 수입 프리미엄이다.
 *
 * 신뢰도를 본국 시세와 **따로** 갖는다 — 일본 현지 거래는 충분한데 국내 체결은
 * 2건뿐인 경우가 흔하다. 화면은 배지를 두 개 그려야 한다.
 */
export interface DomesticQuote {
  /** 원화 체결가 */
  repPrcAmt: number;
  /**
   * 본국 시세 대비 차이 (%). 양수 = 국내가 더 비쌈.
   * `null` = 본국 시세가 산정 불가라 비교 대상이 없음 — 이때 화면은 차이를
   * 말하지 않고 국내 거래가만 보여준다.
   */
  premiumRt: number | null;
  confidence: PriceConfidence;
  tradeCnt: number;
  sources: QuoteSource[];
  lastTradeDt: string;
  updatedHoursAgo: number;
}

/**
 * 개별 실거래 한 건.
 *
 * 대표가·범위·건수는 **전부 이 목록에서 파생된다.** 예전에는 범위를 대표가에
 * 배수로 얹어 따로 만들었는데, 그러면 이력 그래프(추세선)와 범위가 서로를
 * 반박했다 — 화면에는 "6건이 53,700~72,700 에서 팔림" 과 "30일 동안
 * 60,700~63,200 이었음" 이 나란히 떴다. 한 목록에서 뽑으면 어긋날 수 없다.
 */
export interface TradeRecord {
  /** 오늘로부터 며칠 전 (0 = 오늘) */
  daysAgo: number;
  prcAmt: number;
  source: string;
}

/** 실거래를 모으는 창. 이력 그래프의 최장 기간과 같아야 점이 그래프 밖으로 안 나간다. */
export const TRADE_WINDOW_DAYS = 90;

/**
 * 카드 한 장 · 에디션 한 종류의 시세 (PRD §5.8 출력 필드).
 * `repPrcAmt === null` 이면 산정 불가 — 이때 화면은 숫자를 만들어내지 않는다.
 */
export interface EditionQuote {
  edition: Edition;
  /** 원화 대표가격. null = 산정 불가 */
  repPrcAmt: number | null;
  /** 원본 통화 (§5.7 — 먼저 원 시장 통화로 계산한 뒤 환산) */
  originCurrcyCd: "KRW" | "USD" | "JPY";
  originAmt: number | null;
  basis: PriceBasis;
  confidence: PriceConfidence;
  /**
   * 개별 실거래 (최근 `TRADE_WINDOW_DAYS` 일). 최신 → 과거 순.
   * 아래 low/high/tradeCnt 는 전부 여기서 파생된 값이다.
   */
  trades: TradeRecord[];
  /** `trades` 의 최저·최고 (원화) */
  lowPrcAmt: number;
  highPrcAmt: number;
  /** `trades.length` + 출처별 내역 */
  tradeCnt: number;
  sources: QuoteSource[];
  /** 최근 거래일 (YYYY-MM-DD) */
  lastTradeDt: string;
  /** 마지막 수집 갱신 시각 — 상대 표기용 시간 수 (§7 '최근 업데이트 시각' 상시 노출) */
  updatedHoursAgo: number;
  trend7Rt: number;
  trend30Rt: number;
  /** §5.3 대표 시세와 섞지 않는 별도 값들 */
  askPrcAmt: number | null;
  bidPrcAmt: number | null;
  lastTradePrcAmt: number | null;
  /** 산정 불가 사유 (i18n 키 suffix) */
  unavailableReason?: "noTrades";
  /**
   * §5.2 한국 내 거래가. `null` 인 경우가 두 가지이고 화면에서 뜻이 다르다:
   *  - 한국판 — 본국이 곧 국내라 비교할 두 번째 값이 없다 (아무것도 안 그린다)
   *  - 일본판·영문판인데 국내 체결이 없다 (필요하면 "국내 거래 없음" 을 알린다)
   */
  domestic: DomesticQuote | null;
  grades: GradeQuote[];
}

/** 에디션별 원본 통화 (§5.7). */
const EDITION_CURRENCY: Record<Edition, EditionQuote["originCurrcyCd"]> = {
  ko: "KRW",
  en: "USD",
  ja: "JPY",
};

/**
 * 환율이 언제 기준인지 (2026-08-25).
 *
 * 나라별 시세 차이는 **환율에 통째로 흔들린다** — 같은 ¥3,150 이 어제와 오늘
 * 다른 원화가 된다. 그래서 값만 보여 주고 시점을 감추면 그 숫자가 언제 것인지
 * 알 길이 없다. 시세의 '갱신 시각' 과 같은 취급이다(§7 가격 신선도·정직성).
 *
 * ⚠ 더미라 상수다. 수집 서버가 붙으면 **환율을 받아 온 실제 시각**으로 대체해야
 *   한다 — 시세 갱신 시각과 다를 수 있다(환율은 더 자주 바뀐다).
 */
export const FX_UPDATED_HOURS_AGO = 9;

/** 더미 환산 환율 — 서버가 붙으면 수집 시점 환율로 대체된다. */
const FX_TO_KRW: Record<EditionQuote["originCurrcyCd"], number> = {
  KRW: 1,
  USD: 1380,
  JPY: 9.2,
};

/** 에디션별 시장 프리미엄 (더미). 한국판 기준 상대가. */
const EDITION_FACTOR: Record<Edition, number> = { ko: 1, en: 0.82, ja: 0.93 };

/**
 * FN-017 — **표면(FINISH)이 가격 축이다.**
 *
 * 같은 카드라도 논홀로/리버스홀로/홀로포일은 서로 다른 물건으로 거래된다.
 * 아래 값은 논홀로를 1 로 둔 상대가다 (더미 — 서버가 붙으면 표면별 실거래로 대체).
 */
const FINISH_RT: Record<DummyFinish, number> = {
  NORMAL: 1,
  REVERSE_HOLO: 1.4,
  HOLOFOIL: 2.2,
};

/**
 * 카드의 **기본 표면 대비** 배수.
 *
 * ⚠ 절대 배수를 그대로 쓰면 안 된다. 카탈로그의 `variantPrices` 는 이미 그 카드의
 *   기본 표면(=finishCds[0]) 기준으로 매겨진 값이라, 홀로포일 카드에 2.2 를 다시
 *   곱하면 가격이 두 배로 뛴다. 기본 표면을 고르면 1 이 되도록 정규화해서,
 *   표면을 바꾸지 않는 한 다른 화면(홈·검색·컬렉션)의 숫자와 어긋나지 않게 한다.
 */
function finishFactor(card: DummyCard, finish: DummyFinish | undefined): number {
  const base = card.finishCds[0];
  if (!base || !finish || finish === base) return 1;
  return FINISH_RT[finish] / FINISH_RT[base];
}

/**
 * 에디션별 가격 기준 — 영문판은 MVP에서 PriceCharting 현재가(집계 서비스)를 쓴다
 * (PRD §5.1 MVP 제약: TCGplayer 신규 API 발급 불가).
 */
const EDITION_BASIS: Record<Edition, PriceBasis> = {
  ko: "trade",
  en: "aggregate",
  ja: "trade",
};

/**
 * 본국 시세 출처.
 *
 * ⚠ 영문판·일본판 목록에 KREAM 이 없는 것은 **누락이 아니라 규칙**이다 (§5.1·§5.2).
 * KREAM 의 해외판 체결가는 국내 거래가이므로 `DOMESTIC_SOURCES` 로 간다.
 * 한국판은 KREAM 이 곧 본국 1순위 출처라 여기 남는다.
 */
const EDITION_SOURCES: Record<Edition, string[]> = {
  ko: ["KREAM", "번개장터", "네이버 MVC", "당근"],
  en: ["PriceCharting", "eBay"],
  ja: ["SNKRDUNK", "야후옥션", "메루카리"],
};

/** 국내 거래가 출처. 한국판은 본국이 곧 국내라 비워 둔다. */
const DOMESTIC_SOURCES: Record<Edition, string[]> = {
  ko: [],
  en: ["KREAM"],
  ja: ["KREAM"],
};

/**
 * 더미 신뢰도 배정표 — 카탈로그 순서 기준.
 * 4등급(높음/보통/낮음/산정불가)이 모두 화면에 나타나도록 의도적으로 흩뿌렸다.
 */
const CONFIDENCE_MATRIX: PriceConfidence[][] = [
  ["high", "high", "medium"],
  ["high", "medium", "low"],
  ["medium", "low", "none"],
  ["high", "high", "high"],
  ["low", "none", "medium"],
  ["high", "medium", "high"],
];

/** 신뢰도별 표본 규모·산포 (§5.6 기준을 더미로 구현). */
const CONFIDENCE_SHAPE: Record<
  Exclude<PriceConfidence, "none">,
  { tradeCnt: number; spreadRt: number; sourceCnt: number; updatedHoursAgo: number; lastTradeDaysAgo: number }
> = {
  high: { tradeCnt: 14, spreadRt: 0.08, sourceCnt: 3, updatedHoursAgo: 3, lastTradeDaysAgo: 2 },
  medium: { tradeCnt: 6, spreadRt: 0.15, sourceCnt: 2, updatedHoursAgo: 9, lastTradeDaysAgo: 11 },
  low: { tradeCnt: 2, spreadRt: 0.28, sourceCnt: 1, updatedHoursAgo: 26, lastTradeDaysAgo: 64 },
};

/**
 * 국내 프리미엄 더미 배정표 — 카탈로그 순서 기준, 각 행은 `[영문판, 일본판]`.
 * `null` = 국내 체결 없음.
 *
 * 화면이 반드시 다뤄야 하는 네 경우가 모두 한 번씩 나오도록 흩뿌렸다:
 *   ① 국내가 더 비쌈(+)   ② 국내가 더 쌈(−)
 *   ③ 국내 체결 없음      ④ 본국은 산정 불가인데 국내 체결은 있음
 * ④ 는 뮤 VMAX 일본판에서 나온다 (`CONFIDENCE_MATRIX` 3행의 ja 가 "none").
 */
const DOMESTIC_MATRIX: (
  | { premiumRt: number; confidence: Exclude<PriceConfidence, "none"> }
  | null
)[][] = [
  [{ premiumRt: 14.2, confidence: "medium" }, { premiumRt: 11.1, confidence: "high" }],
  [{ premiumRt: 8.6, confidence: "low" }, null],
  [null, { premiumRt: 22.4, confidence: "low" }],
  [{ premiumRt: -3.5, confidence: "medium" }, { premiumRt: 5.2, confidence: "medium" }],
  [null, null],
  [{ premiumRt: 17.8, confidence: "high" }, { premiumRt: 9.4, confidence: "medium" }],
];

/** 더미 기준일 — 테스트가 시계에 의존하지 않도록 고정한다. */
const BASE_DT = "2026-07-29";

function shiftDate(days: number): string {
  const base = new Date(`${BASE_DT}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() - days);
  return base.toISOString().slice(0, 10);
}

/** cardUid 를 안정적인 정수로 — 더미 값이 렌더마다 흔들리지 않게 한다. */
function hash(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h;
}

/**
 * 잘 흩어지는 시드 난수.
 *
 * `hash()` 는 `h*31 + charCode` 라 **끝 한 글자만 다른 시드가 1씩만 차이 난다.**
 * `...trade:0` · `...trade:1` 을 그대로 `% 2000` 하면 난수가 사실상 같은 값이
 * 되어, 실거래 점 여섯 개가 한 자리에 겹쳐 찍혔다. 결과를 한 번 더 섞는다.
 */
function mix(seed: string): number {
  let h = hash(seed);
  h ^= h >>> 16;
  h = Math.imul(h, 0x7feb352d) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x846ca68b) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

function roundTo(amount: number, unit: number): number {
  return Math.round(amount / unit) * unit;
}

/**
 * FN-018 v0.2(rev2) — 등급 축의 **기본 4종**.
 * 이 넷은 거래가 없어도 자리를 지킨다. 사용자가 "PSA 9 는 얼마지" 를 물었을 때
 * 항목이 아예 없으면 '취급 안 함' 으로 읽히기 때문이다.
 * 그 외 감정사·점수(BGS·CGC 등)는 **거래가 있는 것만** 붙인다.
 */
const BASE_GRADES: { graderCd: string; grdCd: string }[] = [
  { graderCd: "RAW", grdCd: "-" },
  { graderCd: "PSA", grdCd: "10" },
  { graderCd: "PSA", grdCd: "9" },
  { graderCd: "PSA", grdCd: "8" },
];

/**
 * 기본 4종 중 실거래가 없는 등급의 추정 배수 (PSA 10 대비).
 * 감정 등급이 한 칸 내려갈 때마다 값이 크게 꺾이는 실제 시장 곡선을 반영한다.
 * ⚠ 어디까지나 더미 — 서버가 붙으면 등급별 실거래가로 대체된다.
 */
const GRADE_FALLBACK_RT: Record<string, number> = { "9": 0.55, "8": 0.32 };

function toGrades(prices: DummyVariantPrice[], factor: number): GradeQuote[] {
  const priced = (graderCd: string, grdCd: string) =>
    prices.find((p) => p.graderCd === graderCd && p.grdCd === grdCd);
  const psa10 = priced("PSA", "10");
  const raw = priced("RAW", "-");

  // <GradeQuote> 명시 — 실거래 분기와 추정 분기의 객체 모양이 달라(trend30Rt
  // number vs null, isEstimated 유무) 추론에 맡기면 두 배열의 유니온이 된다.
  const base = BASE_GRADES.flatMap<GradeQuote>(({ graderCd, grdCd }) => {
    const hit = priced(graderCd, grdCd);
    if (hit) {
      return [{
        graderCd,
        grdCd,
        prcAmt: roundTo(hit.prcAmt * factor, 100),
        trend30Rt: hit.trendRt,
      }];
    }
    // 거래가 없는 PSA 9·8 은 PSA 10(없으면 RAW) 에서 배수로 추정한다.
    // ⚠ 추세는 빌려오지 않는다 — 거래가 없으면 추세도 없다. 앵커의 등락률을
    // 그대로 얹으면 재보지도 않은 숫자를 측정값처럼 내놓는 셈이 된다.
    const anchor = psa10 ?? raw;
    const rt = GRADE_FALLBACK_RT[grdCd];
    if (!anchor || rt === undefined) return [];
    return [{
      graderCd,
      grdCd,
      prcAmt: roundTo(anchor.prcAmt * factor * rt, 100),
      trend30Rt: null,
      isEstimated: true,
    }];
  });

  // 기본 4종에 없는 감정사·점수 — 거래가 있는 것만 뒤에 붙인다
  const extra = prices
    .filter((p) => !BASE_GRADES.some((b) => b.graderCd === p.graderCd && b.grdCd === p.grdCd))
    .map((p) => ({
      graderCd: p.graderCd,
      grdCd: p.grdCd,
      prcAmt: roundTo(p.prcAmt * factor, 100),
      trend30Rt: p.trendRt,
    }));

  return [...base, ...extra];
}

/**
 * 국내 거래가 더미. `homeRep` 은 본국 대표가(원화)이며, 본국이 산정 불가일 때도
 * 프리미엄을 얹을 기준값으로 쓴다 — 다만 그 경우 `premiumRt` 는 `null` 로 둔다.
 * 비교 대상이 없는데 "+22%" 라고 말하면 없는 근거를 지어내는 것이 된다.
 */
function buildDomestic(
  edition: Edition,
  cardIndex: number,
  homeRep: number,
  homeAvailable: boolean,
): DomesticQuote | null {
  const names = DOMESTIC_SOURCES[edition];
  if (names.length === 0) return null; // 한국판 — 본국 = 국내

  const row = DOMESTIC_MATRIX[cardIndex >= 0 ? cardIndex % DOMESTIC_MATRIX.length : 0];
  const entry = row[edition === "en" ? 0 : 1];
  if (!entry) return null; // 국내 체결 없음

  const shape = CONFIDENCE_SHAPE[entry.confidence];
  return {
    repPrcAmt: roundTo(homeRep * (1 + entry.premiumRt / 100), 100),
    premiumRt: homeAvailable ? entry.premiumRt : null,
    confidence: entry.confidence,
    tradeCnt: shape.tradeCnt,
    sources: [{ name: names[0], count: shape.tradeCnt }],
    lastTradeDt: shiftDate(shape.lastTradeDaysAgo),
    updatedHoursAgo: shape.updatedHoursAgo,
  };
}

/**
 * 개별 실거래 더미 생성.
 *
 * 서버가 붙으면 `GET /prices/{cardUid}/trades` 응답으로 대체된다.
 *
 * 규칙 두 가지만 지킨다:
 *  - **가장 최근 건은 정확히 `lastTradeDaysAgo` 일 전**이어야 한다. 화면의
 *    '마지막 거래' 날짜와 그래프의 마지막 점이 같은 날을 가리켜야 한다.
 *  - 산포는 `spreadRt` 를 그대로 쓰되 **최저·최고가 정확히 그 폭에 닿게** 한다.
 *    신뢰도가 낮을수록 점이 넓게 흩어져, 배지가 말하는 것과 눈에 보이는 것이
 *    같아진다.
 */
function buildTrades(
  cardUid: string,
  edition: Edition,
  rep: number,
  trend30Rt: number,
  shape: (typeof CONFIDENCE_SHAPE)[keyof typeof CONFIDENCE_SHAPE],
  sources: QuoteSource[],
): TradeRecord[] {
  const n = sources.reduce((sum, s) => sum + s.count, 0);
  // 출처를 건수만큼 펼쳐 각 거래에 하나씩 배정한다 (앞 출처가 더 많이 나온다).
  const pool = sources.flatMap((s) => Array.from({ length: s.count }, () => s.name));

  // 최근 건(lastTradeDaysAgo)부터 과거로 균등 배치. 1건이면 그 자리 하나뿐이다.
  const step = n > 1 ? (TRADE_WINDOW_DAYS - shape.lastTradeDaysAgo) / (n - 1) : 0;

  /**
   * daysAgo 일 전의 추세값. 30일 변동률을 그대로 되돌린 근사다.
   * ⚠ 오늘 가격 주변에 뿌리면 안 된다 — 오르는 카드에서 90일 전 거래까지 오늘
   * 값 근처에 찍혀, 점 전체가 추세선 아래로 쏠린다("실제로는 다 싸게 팔렸네").
   */
  const at = (daysAgo: number) =>
    rep / Math.max(0.5, 1 + (trend30Rt / 100) * (daysAgo / 30));

  const trades = Array.from({ length: n }, (_, i) => {
    const daysAgo = Math.round(shape.lastTradeDaysAgo + step * i);
    // [-1, 1] 시드 난수 — 같은 (카드·에디션·건번호) 는 항상 같은 값.
    const r = ((mix(`${cardUid}:${edition}:trade:${i}`) % 2001) / 1000) - 1;
    return {
      daysAgo,
      prcAmt: roundTo(at(daysAgo) * (1 + r * shape.spreadRt), 100),
      source: pool[i] ?? sources[0].name,
    };
  });

  // 양 끝을 정확히 ±spreadRt 에 닿게 고정 — 그래야 표기하는 최저·최고가 실제
  // 거래 중 하나가 된다. 난수만 쓰면 전부 가운데 몰려 범위가 좁아진다.
  if (trades.length >= 2) {
    const lo = trades[trades.length - 1];
    const hi = trades[Math.floor(trades.length / 2)];
    lo.prcAmt = roundTo(at(lo.daysAgo) * (1 - shape.spreadRt), 100);
    hi.prcAmt = roundTo(at(hi.daysAgo) * (1 + shape.spreadRt), 100);
  }
  return trades;
}

/** 카드 한 장의 에디션별 시세 3종. 순서는 항상 한국판 → 영문판 → 일본판. */
export function getEditionQuotes(card: DummyCard, finish?: DummyFinish): EditionQuote[] {
  const cardIndex = DUMMY_CARDS.findIndex((c) => c.cardUid === card.cardUid);
  const row = CONFIDENCE_MATRIX[cardIndex >= 0 ? cardIndex % CONFIDENCE_MATRIX.length : 0];
  const seed = hash(card.cardUid);

  return EDITIONS.map((edition, i) => {
    const confidence = row[i];
    // 시장(에디션) × 표면(FINISH) 두 축을 함께 얹는다. 이 factor 하나가 대표가·
    // 등급별·실거래·호가·국내가까지 전부 흐르므로 축이 추가돼도 갈라지지 않는다.
    const factor = EDITION_FACTOR[edition] * finishFactor(card, finish);
    const currcyCd = EDITION_CURRENCY[edition];
    const grades = toGrades(card.variantPrices, factor);
    const base = card.variantPrices[0];

    if (confidence === "none") {
      // 본국 시세가 없어도 국내 체결은 있을 수 있다 — 그 경우 국내 거래가만 보여준다.
      return {
        edition,
        repPrcAmt: null,
        originCurrcyCd: currcyCd,
        originAmt: null,
        basis: EDITION_BASIS[edition],
        confidence,
        trades: [],
        lowPrcAmt: 0,
        highPrcAmt: 0,
        tradeCnt: 0,
        sources: [],
        lastTradeDt: "",
        updatedHoursAgo: CONFIDENCE_SHAPE.low.updatedHoursAgo,
        trend7Rt: 0,
        trend30Rt: 0,
        askPrcAmt: null,
        bidPrcAmt: null,
        lastTradePrcAmt: null,
        unavailableReason: "noTrades" as const,
        domestic: buildDomestic(edition, cardIndex, roundTo(base.prcAmt * factor, 100), false),
        grades: [],
      };
    }

    const shape = CONFIDENCE_SHAPE[confidence];
    const rep = roundTo(base.prcAmt * factor, 100);
    const names = EDITION_SOURCES[edition];
    const sourceCnt = Math.min(shape.sourceCnt, names.length);
    // 출처별 건수 — 앞 출처에 더 실리도록 나눈다 (우선순위 = 가중치, §5.1~5.3)
    const sources: QuoteSource[] = Array.from({ length: sourceCnt }, (_, s) => ({
      name: names[s],
      count: Math.max(1, Math.round((shape.tradeCnt * (sourceCnt - s)) / ((sourceCnt * (sourceCnt + 1)) / 2))),
    }));

    const trades = buildTrades(card.cardUid, edition, rep, base.trendRt, shape, sources);
    const tradePrices = trades.map((tr) => tr.prcAmt);

    return {
      edition,
      repPrcAmt: rep,
      originCurrcyCd: currcyCd,
      originAmt:
        currcyCd === "KRW"
          ? rep
          : Number((rep / FX_TO_KRW[currcyCd]).toFixed(currcyCd === "USD" ? 2 : 0)),
      basis: EDITION_BASIS[edition],
      confidence,
      trades,
      // ↓ 셋 다 trades 에서 파생 — 따로 계산하면 화면에서 서로 어긋난다.
      lowPrcAmt: Math.min(...tradePrices),
      highPrcAmt: Math.max(...tradePrices),
      tradeCnt: trades.length,
      sources,
      lastTradeDt: shiftDate(shape.lastTradeDaysAgo),
      updatedHoursAgo: shape.updatedHoursAgo,
      // 7일 변동은 30일의 일부만 반영된 것으로 둔다 (더미)
      trend7Rt: Number((base.trendRt * 0.35 + ((seed % 7) - 3) * 0.2).toFixed(1)),
      trend30Rt: base.trendRt,
      // 호가 폭은 **신뢰도의 산포를 따라간다.** 고정 배수(±10%/−7%)로 두면 어떤
      // 카드든 스프레드가 같게 나와서, "차이가 크다/작다" 해설이 늘 같은 말을 한다.
      // 거래가 드문 카드일수록 사려는 값과 팔려는 값이 벌어지는 게 실제 시장이다.
      // (고신뢰 ±3.6% → 7% · 보통 ±6.8% → 14% · 낮음 ±12.6% → 25%)
      // ⚠ 반올림 단위는 가격에 비례해야 한다. 싼 카드(2천원대)를 100원 단위로
      //   반올림하면 오차가 퍼센트로 부풀어, 신뢰도 '높음'인데 스프레드가 12% 로
      //   찍히고 화면이 "차이가 큽니다" 라고 말한다.
      askPrcAmt: roundTo(rep * (1 + shape.spreadRt * 0.45), rep >= 10000 ? 100 : 10),
      bidPrcAmt: roundTo(rep * (1 - shape.spreadRt * 0.45), rep >= 10000 ? 100 : 10),
      lastTradePrcAmt: rep,
      domestic: buildDomestic(edition, cardIndex, rep, true),
      grades,
    };
  });
}

/**
 * 목록·그리드용 에디션별 대표가 (원화). `null` = 산정 불가.
 *
 * 검색 결과·세트 카드 그리드가 늘 "한국판 가격"을 보여주면, 진입에서 일본판을 고른
 * 사용자에게 앱이 다른 시장의 숫자를 내미는 셈이 된다 (IA v0.1 rev 진입 플로우).
 */
export function getEditionPrice(card: DummyCard, edition: Edition): number | null {
  return getEditionQuotes(card).find((q) => q.edition === edition)?.repPrcAmt ?? null;
}

/** 이력 시세 그래프에서 고를 수 있는 기간 (PRD §6 Must "이력 그래프"). */
export const HISTORY_PERIODS = [7, 30, 90] as const;
export type HistoryPeriod = (typeof HISTORY_PERIODS)[number];

export interface PriceHistory {
  /** 오래된 날짜 → 최근 날짜 순의 일별 대표가 (원화) */
  points: number[];
  /** 기간 시작 대비 변동률 (%) */
  changeRt: number;
  /** 추세선의 최저·최고 */
  lowPrcAmt: number;
  highPrcAmt: number;
  /**
   * 이 기간 안에 실제로 체결된 건 (StockX 방식 — 선 위에 점으로 얹는다).
   * 추세선은 매끄러운 추정이고 이 점들이 실제 값이다. 둘을 같이 보여줘야
   * "왜 범위가 그래프보다 넓지" 하는 의문이 안 생긴다.
   */
  trades: TradeRecord[];
  /** 선과 점을 **모두** 담는 세로 범위. 이걸 벗어나면 점이 그래프 밖으로 나간다. */
  axisMin: number;
  axisMax: number;
}

/**
 * 이력 시세 — 더미 생성기.
 *
 * 서버가 붙으면 이 함수는 `GET /prices/{cardUid}/history?edition=&days=` 응답으로
 * 대체된다. 값은 cardUid+edition 으로 시드를 고정해 렌더마다 흔들리지 않는다.
 *
 * ★ 화면 안에서 숫자가 서로 어긋나면 안 된다. 그래서 추세선을 두 구간으로 만든다:
 *   - 최근 7일 : `trend7Rt` 를 정확히 재현
 *   - 7~30일   : 30일 전 값이 `trend30Rt` 와 정확히 맞도록 보간
 *   - 30일 이전: 7~30 구간의 기울기를 그대로 연장 (90일 보기용)
 * 잔물결(noise)은 구간 **양 끝점에는 넣지 않는다.** 끝점에 노이즈가 끼면
 * 그래프가 읽는 변동률이 상단 요약의 7일/30일 변동률과 달라진다.
 */
export function getPriceHistory(
  cardUid: string,
  quote: EditionQuote,
  days: HistoryPeriod,
): PriceHistory | null {
  if (quote.repPrcAmt === null) return null;

  const end = quote.repPrcAmt;
  const ratio7 = Math.max(0.5, 1 + quote.trend7Rt / 100);
  const ratio30 = Math.max(0.5, 1 + quote.trend30Rt / 100);
  const v7 = end / ratio7;   // 7일 전 값
  const v30 = end / ratio30; // 30일 전 값

  // 세 기준점 (오늘=end, 7일 전=v7, 30일 전=v30) 을 정확히 지나는 매끄러운 곡선을
  // 로그가격 기준 2차식으로 만든다. 구간을 잘라 이어 붙이면 7일 지점에서 꺾여
  // 없는 이벤트가 있었던 것처럼 보인다.
  const L0 = Math.log(end);
  const a7 = Math.log(v7) - L0;
  const a30 = Math.log(v30) - L0;
  const c = (a30 - (30 * a7) / 7) / 690;
  const b = (a7 - 49 * c) / 7;
  const slope30 = b + 60 * c; // 30일 지점의 기울기 — 그 이전은 직선으로 연장

  /** daysAgo 일 전의 추세선 값 (노이즈 제외). */
  const drift = (daysAgo: number): number => {
    const logValue = daysAgo <= 30
      ? L0 + b * daysAgo + c * daysAgo * daysAgo
      : Math.log(v30) + slope30 * (daysAgo - 30);
    return Math.exp(logValue);
  };

  /** [-0.5, 0.5] 시드 난수 — 같은 (카드·에디션·일자) 는 항상 같은 값. */
  const rand = (daysAgo: number) =>
    ((hash(`${cardUid}:${quote.edition}:${daysAgo}`) % 1000) / 1000) - 0.5;
  /**
   * 이웃 값을 섞어 매끄럽게 만든 잔물결. 하루짜리 백색소음을 그대로 쓰면 점 하나가
   * 위아래로 튀어 실제로는 없었던 급등락처럼 읽힌다.
   */
  const wobble = (daysAgo: number) =>
    (rand(daysAgo - 2) + 2 * rand(daysAgo - 1) + 3 * rand(daysAgo) +
     2 * rand(daysAgo + 1) + rand(daysAgo + 2)) / 9;

  // 잔물결의 크기는 그 카드의 실제 변동폭에 비례해야 한다. 고정 ±2% 로 두면
  // 30일에 0.8% 밖에 안 움직인 카드에서 노이즈가 추세를 덮어버려, 없는 급등락이
  // 있는 것처럼 보인다.
  const noiseAmp = Math.min(0.02, Math.max(0.003, (Math.abs(quote.trend30Rt) / 100) * 0.35));

  // d = days(가장 오래된) … 0(오늘) → 점은 days+1 개. "30일 보기"가 실제로 30일을
  // 건너뛰어야 상단 요약의 30일 변동률과 같은 구간을 말하게 된다.
  const points: number[] = [];
  for (let d: number = days; d >= 0; d -= 1) {
    const i = days - d; // 0 = 가장 오래된 점
    // 시드 기반 잔물결 — 실제 거래 산포처럼 보이되 매 렌더 동일하다.
    // 양 끝에서 0 으로 사라지는 창(sin)을 씌워, 마지막 점이 튀어 보이거나
    // 첫 점이 흔들려 변동률이 어긋나는 걸 막는다.
    const taper = Math.sin((Math.PI * i) / days);
    const noise = 1 + wobble(d) * 2 * noiseAmp * taper;
    // 그래프용 점은 100원 단위로 반올림하지 않는다 — 변동 폭이 작은 카드에서
    // 곡선이 계단처럼 꺾인다. 반올림은 아래 표시용 최저/최고에만 적용한다.
    points.push(d === 0 ? end : drift(d) * noise);
  }

  // 변동률은 반올림된 점이 아니라 추세선 자체로 계산한다 — 그래야 7일/30일 보기가
  // 상단 요약의 7일/30일 변동률과 정확히 같은 숫자를 말한다.
  const base = drift(days);

  // 이 기간 안의 실거래만 얹는다. 7일 보기에서는 한 건도 없을 수 있다 —
  // 없는 점을 지어내지 않고 선만 그린다.
  const trades = quote.trades.filter((tr) => tr.daysAgo <= days);

  // 세로 범위는 **선과 점을 모두** 담아야 한다. 선만 기준으로 잡으면 산포가 큰
  // 카드에서 실거래 점이 그래프 위아래로 잘려 나간다.
  const all = [...points, ...trades.map((tr) => tr.prcAmt)];
  const rawMin = Math.min(...all);
  const rawMax = Math.max(...all);
  const pad = (rawMax - rawMin) * 0.08 || rawMax * 0.02;

  return {
    points,
    changeRt: base > 0 ? Number((((end - base) / base) * 100).toFixed(1)) : 0,
    lowPrcAmt: roundTo(Math.min(...points), 100),
    highPrcAmt: roundTo(Math.max(...points), 100),
    trades,
    axisMin: rawMin - pad,
    axisMax: rawMax + pad,
  };
}

/**
 * §5.7 화폐 표시 — **원화 금액을 그 시장의 원본 통화로 되돌린 표기.**
 *
 * PRD 는 "원본 가격과 원화 환산가격을 함께 표시"하라고 못박고 예시를 준다:
 *   '$28.70 약 42,000원' · '¥3,150 약 31,500원'
 * 즉 원화만 단독으로 내걸면 안 된다. 영문판·일본판 금액은 원 시장 통화로 계산된 값을
 * 환산한 것이므로, 원본을 지우면 어디서 온 숫자인지 사라지고 환산 오차도 감춰진다.
 *
 * 한국판은 원본이 곧 원화라 `null` (병기할 두 번째 값이 없다).
 */
export function formatOriginAmount(
  krwAmount: number,
  currcyCd: EditionQuote["originCurrcyCd"],
): string | null {
  if (currcyCd === "KRW") return null;
  const origin = krwAmount / FX_TO_KRW[currcyCd];
  if (currcyCd === "USD") {
    return `$${origin.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `¥${Math.round(origin).toLocaleString("ja-JP")}`;
}

/** 대표 시세의 원본 통화 표기 — "$28.70" / "¥3,150" / null(한국판). */
export function formatOriginPrice(quote: EditionQuote): string | null {
  if (quote.originAmt === null || quote.originCurrcyCd === "KRW") return null;
  if (quote.originCurrcyCd === "USD") {
    return `$${quote.originAmt.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `¥${Math.round(quote.originAmt).toLocaleString("ja-JP")}`;
}

/**
 * 국내 프리미엄 표기 — "+11.1%" / "−3.5%" / null(비교 대상 없음).
 *
 * ⚠ 이 값을 등락(`Trend`) 컴포넌트로 그리지 말 것. 등락은 상승=초록·하락=빨강의
 * 손익 관습을 달고 오는데, 프리미엄은 손익이 아니라 두 시장의 가격 차이일 뿐이다.
 * "+22%" 를 빨갛게 칠하면 "지금 사면 손해" 라는 조언이 되어 버린다 — 시세 앱이
 * 할 말이 아니다. 중립 색 + 라벨로 사실만 표시한다.
 */
export function formatPremiumRt(rt: number | null): string | null {
  if (rt === null) return null;
  return `${rt > 0 ? "+" : rt < 0 ? "−" : ""}${Math.abs(rt).toFixed(1)}%`;
}

/** §3.2 날짜 표기 통일 — 2026.07.26 */
export function formatDotDate(isoDate: string): string {
  return isoDate.replaceAll("-", ".");
}

/** 신뢰도 → Badge variant. 색만으로 구분하지 않도록 라벨은 항상 함께 쓴다. */
export const CONFIDENCE_BADGE: Record<PriceConfidence, "success" | "info" | "warning" | "neutral"> = {
  high: "success",
  medium: "info",
  low: "warning",
  none: "neutral",
};
